import React from "react";
import { withTranslation } from "react-i18next";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import {
	Row,
	Col,
	FormGroup,
	FormLabel,
	FormControl,
} from "react-bootstrap";
import Switch from "components/Switch";
import Loading from "components/Loading";
import InlineAlert from "components/InlineAlert";
import { employeesService, payflowIntegrationService } from "services";
import { errorFormating } from "utils/utils";
import { formatIban, isValidIban, normalizeIban } from "utils/iban";

const MASKED_VALUE_PATTERN = /[*•]/;

class EmployeePayroll extends React.Component {
	constructor(props) {
		super(props);
		this.state = this.buildStateFromEmployee(props.employee);
	}

	formatEmployeeForForm(employee) {
		const safeEmployee = employee || {};
		return {
			...safeEmployee,
			iban: safeEmployee.iban ? formatIban(safeEmployee.iban) : "",
		};
	}

	componentDidMount() {
		this.loadPayrollContext();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.employee?.id !== this.props.employee?.id) {
			this.setState(this.buildStateFromEmployee(this.props.employee), () => {
				this.loadPayrollContext();
			});
		}
	}

	buildStateFromEmployee(employee) {
		const safeEmployee = this.formatEmployeeForForm(employee);
		return {
			employee: { ...safeEmployee },
			prevEmployee: { ...safeEmployee },
			companyPayflowActive: false,
			loadingContext: true,
			saving: false,
			errors: {},
			ibanDirty: false,
			netSalaryDirty: false,
			latestSyncError: null,
			latestSuccessSyncAt: null,
		};
	}

	loadPayrollContext = async () => {
		const { employee } = this.props;
		if (!employee?.id) {
			this.setState({ loadingContext: false });
			return;
		}

		this.setState({ loadingContext: true });

		const [integrationsResponse, logsResponse] = await Promise.all([
			payflowIntegrationService.getIntegrations(),
			payflowIntegrationService.getSyncLogs({ employee_id: employee.id }),
		]);

		const integrations =
			integrationsResponse?.data || integrationsResponse || {};
		const logs = Array.isArray(logsResponse?.data)
			? logsResponse.data
			: Array.isArray(logsResponse)
				? logsResponse
				: [];

		const sortedLogs = [...logs].sort(
			(a, b) => new Date(b.created || 0) - new Date(a.created || 0)
		);
		const latestError = sortedLogs.find(
			(log) => String(log.type || "").toUpperCase() === "ERROR"
		);
		const latestSuccess = sortedLogs.find((log) => {
			const type = String(log.type || "").toUpperCase();
			return type === "CREATED" || type === "UPDATED" || type === "SUCCESS";
		});

		this.setState({
			loadingContext: false,
			companyPayflowActive: Boolean(integrations.payflow_active),
			latestSyncError: latestError?.description || null,
			latestSuccessSyncAt: latestSuccess?.created || null,
		});
	};

	handleChange = (event) => {
		const { id, value } = event.target;
		const { employee } = this.state;

		if (id === "iban") {
			this.setState({
				employee: { ...employee, iban: formatIban(value) },
				ibanDirty: true,
				errors: { ...this.state.errors, iban: "" },
			});
			return;
		}

		if (id === "net_salary") {
			this.setState({
				employee: { ...employee, net_salary: value },
				netSalaryDirty: true,
				errors: { ...this.state.errors, net_salary: "" },
			});
			return;
		}

		this.setState({
			employee: { ...employee, [id]: value },
		});
	};

	handleSwitch = (event) => {
		const { id, checked } = event.target;
		const value = id === "payflow_active" ? !checked : checked;
		this.setState(
			(prev) => ({
				employee: { ...prev.employee, [id]: value },
			}),
			() => this.saveEmployee()
		);
	};

	handleBlur = () => {
		const hasChanged =
			JSON.stringify(this.state.employee) !==
			JSON.stringify(this.state.prevEmployee);
		if (hasChanged) {
			this.saveEmployee();
		}
	};

	validateFields = () => {
		const { t } = this.props;
		const { employee, ibanDirty, netSalaryDirty } = this.state;
		const errors = {};

		const ibanValue = employee.iban || "";
		const shouldValidateIban =
			ibanDirty || (ibanValue && !MASKED_VALUE_PATTERN.test(ibanValue));
		if (shouldValidateIban && ibanValue && !isValidIban(ibanValue)) {
			errors.iban = t("employeePayroll.errors.invalidIban");
		}

		const netSalaryValue = employee.net_salary;
		const shouldValidateSalary =
			netSalaryDirty ||
			(netSalaryValue !== "" &&
				netSalaryValue != null &&
				!MASKED_VALUE_PATTERN.test(String(netSalaryValue)));
		if (shouldValidateSalary && netSalaryValue !== "" && netSalaryValue != null) {
			const parsed = Number(String(netSalaryValue).replace(",", "."));
			if (Number.isNaN(parsed) || parsed < 0) {
				errors.net_salary = t("employeePayroll.errors.invalidNetSalary");
			}
		}

		return errors;
	};

	saveEmployee = async () => {
		const { t, onEmployeeUpdated } = this.props;
		const {
			employee,
			saving,
			ibanDirty,
			netSalaryDirty,
			prevEmployee,
		} = this.state;
		if (saving || !employee?.id) return;

		const validationErrors = this.validateFields();
		if (Object.keys(validationErrors).length > 0) {
			this.setState({ errors: validationErrors });
			return;
		}

		this.setState({ saving: true, errors: {} });

		const payload = {
			...employee,
			iban: employee.iban ? normalizeIban(employee.iban) : "",
			net_salary:
				netSalaryDirty && employee.net_salary !== ""
					? Number(String(employee.net_salary).replace(",", "."))
					: prevEmployee.net_salary,
		};

		const response = await employeesService.update(payload);

		if (!response || response.ok === false) {
			const errs = errorFormating(response);
			this.setState({
				saving: false,
				errors: errs,
			});
			toastr.error(t("common.error"), errs.general || t("common.errorSaving"));
			return;
		}

		const refreshed = await employeesService.getById(employee.id);
		const updatedEmployee =
			refreshed && refreshed.ok !== false ? refreshed : response.data || response;
		const formattedUpdatedEmployee = this.formatEmployeeForForm(updatedEmployee);
		toastr.success(t("common.success"), t("common.changesSaved"));

		this.setState({
			saving: false,
			employee: formattedUpdatedEmployee,
			prevEmployee: { ...formattedUpdatedEmployee },
			ibanDirty: false,
			netSalaryDirty: false,
		});

		if (typeof onEmployeeUpdated === "function") {
			onEmployeeUpdated(updatedEmployee);
		}

		this.loadPayrollContext();
	};

	formatSyncDate = (value) => {
		if (!value) return "—";
		const m = moment.utc(value).local();
		return m.isValid() ? m.format("D MMMM YYYY - HH:mm") : "—";
	};

	render() {
		const { t } = this.props;
		const {
			employee,
			companyPayflowActive,
			loadingContext,
			saving,
			errors,
			latestSyncError,
			latestSuccessSyncAt,
		} = this.state;

		if (loadingContext) {
			return <Loading />;
		}

		const lastSyncAt =
			employee.payflow_last_sync_at ||
			employee.payflow_last_sync ||
			employee.last_sync_at ||
			latestSuccessSyncAt;
		const syncError =
			latestSyncError ||
			employee.payflow_sync_error ||
			employee.payflow_last_error ||
			errors.general;

		const onLeave = !Boolean(employee.payflow_active);

		return (
			<div className="employee-payroll">
				<h3 className="section-title">{t("employeePayroll.title")}</h3>

				{!companyPayflowActive && (
					<InlineAlert variant="info" layout="banner" className="mb-3">
						{t("employeePayroll.companyIntegrationInactive")}
					</InlineAlert>
				)}

				<div className="info-section">
					<Row>
						<Col md={6}>
							<FormGroup className="form-group" controlId="net_salary">
								<FormLabel>
									{t("employeePayroll.netSalary")}{" "}
									<span className="required">*</span>
								</FormLabel>
								<div className="employee-payroll__currency-input">
									<FormControl
										type="text"
										inputMode="decimal"
										value={
											employee.net_salary != null
												? String(employee.net_salary)
												: ""
										}
										onChange={this.handleChange}
										onBlur={this.handleBlur}
										placeholder={t("employeePayroll.netSalaryPlaceholder")}
										className="form-control-modern"
										disabled={saving}
										isInvalid={Boolean(errors.net_salary)}
									/>
									<span className="employee-payroll__currency-symbol">€</span>
								</div>
								<FormControl.Feedback type="invalid">
									{errors.net_salary}
								</FormControl.Feedback>
							</FormGroup>
						</Col>

						<Col md={6}>
							<FormGroup className="form-group" controlId="iban">
								<FormLabel>
									{t("employeePayroll.iban")}{" "}
									<span className="required">*</span>
								</FormLabel>
								<FormControl
									type="text"
									value={employee.iban || ""}
									onChange={this.handleChange}
									onBlur={this.handleBlur}
									placeholder={t("employeePayroll.ibanPlaceholder")}
									className="form-control-modern"
									disabled={saving}
									isInvalid={Boolean(errors.iban)}
									autoComplete="off"
								/>
								<FormControl.Feedback type="invalid">
									{errors.iban}
								</FormControl.Feedback>
							</FormGroup>
						</Col>
					</Row>
				</div>

				{companyPayflowActive && (
					<div className="info-section employee-payroll__payflow-section">
						<h4 className="employee-payroll__subsection-title">Payflow</h4>

						<FormGroup
							className="form-group form-group-flex form-row-switch employee-payroll__onleave-switch-row"
							controlId="payflow_active"
						>
							<FormLabel className="employee-payroll__onleave-switch-label">
								{t("employeePayroll.onLeaveHelp")}
							</FormLabel>
							<Switch
								controlId="payflow_active"
								isOn={onLeave}
								handleToggle={this.handleSwitch}
								disabled={saving}
							/>
						</FormGroup>

						<div className="employee-payroll__sync-info">
							<div>
								<span className="employee-payroll__sync-label">
									{t("employeePayroll.lastSync")}
								</span>
								<span className="employee-payroll__sync-value">
									{this.formatSyncDate(lastSyncAt)}
								</span>
							</div>
						</div>

						{syncError && (
							<InlineAlert variant="error" layout="banner" className="mt-3">
								{syncError}
							</InlineAlert>
						)}
					</div>
				)}
			</div>
		);
	}
}

export default withTranslation()(EmployeePayroll);
